// Transforms the verbatim listing-checklist CSV (see ./starterChecklistRaw.js)
// into a Task Template object: { [stageName]: [ item, ... ] } where each item is
// either a section header { type:"section", id, title } or a task
// { type:"task", id, title, status, deadline, attachments, assignee,
//   coordinatorRole, createdAt }.
//
// CSV shape: each line has two columns. A non-empty column A is a HEADER (a stage
// name or a numbered sub-section). An empty column A (line starts with ",") is a
// TASK under the most recent header. Column B may be wrapped in double quotes when
// it contains commas; "" inside a quoted field is an escaped ".

// The five stage names that map 1:1 to a stage bucket of the same name.
const STAGE_HEADERS = new Set([
  "CMAs",
  "Listing Appointment",
  "Under Contract",
  "Pending",
  "Closed",
]);

// Numbered sub-section header, e.g. "3 - Documents Needed From Seller (0/7)".
// Group 1 captures the Name; the trailing "(x/y)" progress counter is optional.
const SUB_SECTION_RE = /^\d+\s*-\s*(.+?)(?:\s*\(\d+\/\d+\))?$/;

// Leading role prefix on a task title, e.g. "LC: ", "EA/LC: ".
const ROLE_PREFIX_RE = /^(LC\/EA|EA\/LC|TC\/EA|EA\/TC|LC|TC|EA):\s*/i;

// Split a CSV line into [colA, colB] on the FIRST comma (column A never contains
// a comma). If colB is wrapped in double quotes, strip them and unescape "" -> ".
function splitLine(line) {
  const i = line.indexOf(",");
  if (i === -1) return [line, ""];
  const colA = line.slice(0, i);
  let colB = line.slice(i + 1);
  if (colB.length >= 2 && colB.startsWith('"') && colB.endsWith('"')) {
    colB = colB.slice(1, -1).replace(/""/g, '"');
  }
  return [colA, colB];
}

export function parseChecklistCsv(csv) {
  const result = {};
  let currentStage = null;
  let currentSection = null; // reserved for future use; tasks always push to the stage array
  let nextId = 1;

  const ensureStage = (stage) => {
    if (!result[stage]) result[stage] = [];
    return result[stage];
  };

  const lines = String(csv).split("\n");

  for (const rawLine of lines) {
    if (rawLine.trim() === "") continue; // ignore blank lines

    const [colA, colB] = splitLine(rawLine);

    if (colA !== "") {
      // Header row.
      if (STAGE_HEADERS.has(colA)) {
        currentStage = colA;
        currentSection = null;
        ensureStage(currentStage);
        continue;
      }

      const m = colA.match(SUB_SECTION_RE);
      if (m) {
        const name = m[1].trim();
        if (name.toUpperCase() === "COMING SOON") {
          // Tasks are loose under "Coming Soon" with NO section header.
          currentStage = "Coming Soon";
          currentSection = null;
          ensureStage(currentStage);
        } else {
          // A Pre-Market sub-section: push a section header and set it current.
          currentStage = "Pre-Market";
          currentSection = name;
          const arr = ensureStage(currentStage);
          arr.push({ type: "section", id: nextId++, title: name });
        }
        continue;
      }

      // Unrecognized header: treat as a stage of that exact name (defensive).
      currentStage = colA;
      currentSection = null;
      ensureStage(currentStage);
      continue;
    }

    // Task row (empty column A).
    if (currentStage === null) continue; // task before any header: skip

    let title = colB;
    let coordinatorRole = false;
    let transactionCoordinatorRole = false;
    const pm = title.match(ROLE_PREFIX_RE);
    if (pm) {
      coordinatorRole = /LC/i.test(pm[1]);
      transactionCoordinatorRole = /TC/i.test(pm[1]);
      title = title.slice(pm[0].length);
    }
    title = title.trim();

    ensureStage(currentStage).push({
      type: "task",
      id: nextId++,
      title,
      status: "Open",
      deadline: "",
      attachments: [],
      assignee: null,
      coordinatorRole,
      transactionCoordinatorRole,
      createdAt: null,
    });
  }

  return result;
}
